import React, { useState } from "react";
import {
    Alert,
    Button,
    Card,
    Input,
    Pagination,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from "antd";
import { DatabaseOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useGetAdminBrandsQuery } from "../../services/nestjs-base-api";
import { IAdminBrandResponse } from "../../services/nestjs-base-api";
import { useAuth } from "../../hooks/useAuth";

const { Title, Text } = Typography;
const { Search } = Input;

/**
 * SYR-78 — admin inspector list. Cross-org brand browser; click through to see a
 * brand's raw Knowledge Base inputs and the exact synthesised {BRAND_KNOWLEDGE}.
 */
export const BrandKnowledgeInspectorPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [limit] = useState(12);
    const [search, setSearch] = useState("");

    const { data, isLoading, isFetching, error } = useGetAdminBrandsQuery({
        jwtToken: user?.jwtToken || "",
        page,
        limit,
        search: search || undefined,
    });

    const brands = data?.data || [];
    const total = data?.total || 0;

    const columns = [
        {
            title: "Brand",
            dataIndex: "name",
            key: "name",
            render: (name: string, brand: IAdminBrandResponse) => (
                <Space>
                    <Text strong>{name}</Text>
                    {brand.isMock && <Tag color="orange">MOCK</Tag>}
                </Space>
            ),
        },
        {
            title: "Organisation",
            dataIndex: "organisationName",
            key: "organisationName",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: string) => (
                <Tag color={status?.toLowerCase() === "active" ? "green" : "red"}>
                    {status?.toUpperCase() || "UNKNOWN"}
                </Tag>
            ),
        },
        {
            title: "",
            key: "action",
            align: "right" as const,
            render: (_: unknown, brand: IAdminBrandResponse) => (
                <Button
                    icon={<EyeOutlined />}
                    onClick={() =>
                        navigate(`/admin/ai/knowledge_base/brands/${brand.id}`)
                    }
                >
                    View knowledge
                </Button>
            ),
        },
    ];

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    type="error"
                    showIcon
                    message="Error loading brands"
                    description="Unable to fetch brands. Please try again later."
                />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24,
                }}
            >
                <Title level={3} style={{ margin: 0 }}>
                    <DatabaseOutlined style={{ marginRight: 8 }} />
                    Brand Knowledge Inspector
                </Title>
                <Text type="secondary">
                    {total} brand{total !== 1 ? "s" : ""}
                </Text>
            </div>

            <Card style={{ marginBottom: 24 }}>
                <Text type="secondary">
                    Inspect any brand's Knowledge Base — the raw context they've
                    provided and the exact synthesised block injected into AI
                    prompts via {"{BRAND_KNOWLEDGE}"}.
                </Text>
            </Card>

            <Card>
                <Search
                    placeholder="Search brands by name or organisation..."
                    allowClear
                    size="large"
                    onSearch={(value) => {
                        setSearch(value);
                        setPage(1);
                    }}
                    style={{ maxWidth: 400, marginBottom: 16 }}
                />
                {isLoading || isFetching ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={brands}
                        pagination={false}
                    />
                )}
                {total > limit && (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                        <Pagination
                            current={page}
                            total={total}
                            pageSize={limit}
                            onChange={setPage}
                            showSizeChanger={false}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
};
